import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { isRequestCancellation } from "@shared/api/client";
import {
  getGroupChats,
  type GroupChatGroup,
  type GroupChatScope,
} from "@shared/api/groupChat";

type GroupChatHubContextValue = {
  scope: GroupChatScope;
  groups: GroupChatGroup[];
  loadingGroups: boolean;
  groupsError: string | null;
  selectedGroupId: number | null;
  selectGroup: (groupId: number | null) => void;
  refreshGroups: (quiet?: boolean) => Promise<GroupChatGroup[]>;
  patchGroup: (groupId: number, patch: Partial<GroupChatGroup>) => void;
  createRequestId: number;
  createBusy: boolean;
  requestCreate: () => void;
  setCreateBusy: (busy: boolean) => void;
};

const GroupChatHubContext = createContext<GroupChatHubContextValue | null>(null);

export function useGroupChatHub(): GroupChatHubContextValue {
  const value = useContext(GroupChatHubContext);
  if (!value) {
    throw new Error("useGroupChatHub debe utilizarse dentro de ChatHub.");
  }
  return value;
}

export function ChatHub({
  scope,
  direct,
}: {
  scope: GroupChatScope;
  floating?: boolean;
  direct: ReactNode;
}) {
  const [groups, setGroups] = useState<GroupChatGroup[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [createRequestId, setCreateRequestId] = useState(0);
  const [createBusy, setCreateBusy] = useState(false);
  const groupsRef = useRef<GroupChatGroup[]>([]);
  const refreshPromiseRef = useRef<Promise<GroupChatGroup[]> | null>(null);

  const applyGroups = useCallback((next: GroupChatGroup[]) => {
    groupsRef.current = next;
    setGroups(next);
    setSelectedGroupId((current) =>
      current !== null && !next.some((group) => group.id === current) ? null : current,
    );
  }, []);

  const refreshGroups = useCallback(
    async (quiet = false): Promise<GroupChatGroup[]> => {
      if (refreshPromiseRef.current) {
        return refreshPromiseRef.current;
      }

      if (!quiet && groupsRef.current.length === 0) setLoadingGroups(true);

      const request = (async (): Promise<GroupChatGroup[]> => {
        try {
          const next = await getGroupChats(scope);
          applyGroups(next);
          setGroupsError(null);
          return next;
        } catch (error) {
          if (!isRequestCancellation(error) && (!quiet || groupsRef.current.length === 0)) {
            setGroupsError("No se pudieron cargar los grupos de chat.");
          }
          return groupsRef.current;
        } finally {
          setLoadingGroups(false);
        }
      })();

      refreshPromiseRef.current = request;
      try {
        return await request;
      } finally {
        if (refreshPromiseRef.current === request) refreshPromiseRef.current = null;
      }
    },
    [applyGroups, scope],
  );

  const patchGroup = useCallback((groupId: number, patch: Partial<GroupChatGroup>) => {
    const next = groupsRef.current.map((group) =>
      group.id === groupId ? { ...group, ...patch } : group,
    );
    groupsRef.current = next;
    setGroups(next);
  }, []);

  const selectGroup = useCallback((groupId: number | null) => {
    setSelectedGroupId(groupId);
  }, []);

  const requestCreate = useCallback(() => {
    if (createBusy) return;
    setCreateBusy(true);
    setCreateRequestId((current) => current + 1);
  }, [createBusy]);

  useEffect(() => {
    void refreshGroups(false);
  }, [refreshGroups]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      void refreshGroups(true);
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [refreshGroups]);

  const value = useMemo<GroupChatHubContextValue>(
    () => ({
      scope,
      groups,
      loadingGroups,
      groupsError,
      selectedGroupId,
      selectGroup,
      refreshGroups,
      patchGroup,
      createRequestId,
      createBusy,
      requestCreate,
      setCreateBusy,
    }),
    [
      createBusy,
      createRequestId,
      groups,
      groupsError,
      loadingGroups,
      patchGroup,
      refreshGroups,
      requestCreate,
      scope,
      selectGroup,
      selectedGroupId,
    ],
  );

  return <GroupChatHubContext.Provider value={value}>{direct}</GroupChatHubContext.Provider>;
}
